const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const TOTAL_MINUTES_IN_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;
const HALF_DAY_MINUTES = 720;
const MIDDAY_HOUR = 12;
const DEFAULT_INTERVAL = 15;
export const itemToString = (option) => {
    return option?.label || '';
};
export const generateTimeOptions = (is24hFormat = false, interval = DEFAULT_INTERVAL) => {
    const options = [];
    for (let mCount = 0; mCount < TOTAL_MINUTES_IN_DAY; mCount += interval) {
        const label = formatMinutesToTime(mCount, is24hFormat);
        options.push({
            id: `time-${mCount}`,
            label,
            value: label,
            minutes: mCount,
        });
    }
    return options;
};
export const normalizeTime = (time) => {
    return time.toLowerCase().replace(/\s/g, '').replace(/am/g, 'a').replace(/pm/g, 'p');
};
const applyAmPmLogic = (hour, isAm, isPm) => {
    let adjustedHour = hour;
    if (adjustedHour === MIDDAY_HOUR) {
        if (isAm && !isPm)
            adjustedHour = 0;
        else if (isPm)
            adjustedHour = MIDDAY_HOUR;
    }
    else if (isPm && adjustedHour < MIDDAY_HOUR) {
        adjustedHour += MIDDAY_HOUR;
    }
    else if (isAm && adjustedHour === MIDDAY_HOUR) {
        adjustedHour = 0;
    }
    return adjustedHour;
};
const parseColonTime = (timePart) => {
    const parts = timePart.split(':');
    const hour = parseInt(parts[0], 10);
    const minStr = parts[1];
    const minute = minStr.length === 1 ? parseInt(minStr, 10) * 10 : parseInt(minStr, 10);
    return { hour, minute };
};
const parseNumericTime = (timePart) => {
    const numbers = timePart.replace(/[^0-9]/g, '');
    if (!numbers)
        return { hour: NaN, minute: NaN };
    if (numbers.length <= 2) {
        return { hour: parseInt(numbers, 10), minute: 0 };
    }
    const hourPart = numbers.slice(0, numbers.length - 2);
    const minPart = numbers.slice(numbers.length - 2);
    return { hour: parseInt(hourPart, 10), minute: parseInt(minPart, 10) };
};
export const parseInputToMinutes = (input, is24hFormat = false) => {
    const normalized = normalizeTime(input);
    const isPm = normalized.includes('p');
    const isAm = normalized.includes('a');
    const timePart = normalized.replace(/[ap]/g, '');
    const { hour: rawHour, minute } = timePart.includes(':')
        ? parseColonTime(timePart)
        : parseNumericTime(timePart);
    if (isNaN(rawHour) || isNaN(minute))
        return null;
    let hour = rawHour;
    if (!is24hFormat) {
        hour = applyAmPmLogic(rawHour, isAm, isPm);
    }
    else {
        if (isPm && hour < MIDDAY_HOUR)
            hour += MIDDAY_HOUR;
        else if (isAm && hour === MIDDAY_HOUR)
            hour = 0;
    }
    return Math.min(hour * MINUTES_PER_HOUR + minute, TOTAL_MINUTES_IN_DAY - 1);
};
export const formatMinutesToTime = (totalMinutes, is24hFormat = false) => {
    let hour = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = totalMinutes % MINUTES_PER_HOUR;
    const minutesStr = minutes.toString().padStart(2, '0');
    if (is24hFormat) {
        return `${hour.toString().padStart(2, '0')}:${minutesStr}`;
    }
    const period = hour >= MIDDAY_HOUR ? 'pm' : 'am';
    hour = hour % MIDDAY_HOUR;
    if (hour === 0)
        hour = MIDDAY_HOUR;
    return `${hour}:${minutesStr} ${period}`;
};
export const formatMinutesToHHmmss = (totalMinutes) => {
    const hour = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = totalMinutes % MINUTES_PER_HOUR;
    return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};
export const timeFilter = (inputValue, options, is24hFormat = false) => {
    if (!inputValue)
        return options;
    const normalizedInput = normalizeTime(inputValue);
    const filtered = options.filter((item) => {
        const normalizedLabel = normalizeTime(item.label);
        const labelNoColon = normalizedLabel.replace(/[:]/g, '');
        const labelNoPeriod = normalizedLabel.replace(/[ap:]/g, '');
        return (normalizedLabel.startsWith(normalizedInput) ||
            labelNoColon.startsWith(normalizedInput) ||
            labelNoPeriod.startsWith(normalizedInput) ||
            normalizedLabel.includes(normalizedInput));
    });
    if (filtered.length > 0)
        return filtered.sort((a, b) => a.minutes - b.minutes);
    const inputMinutes = parseInputToMinutes(inputValue, is24hFormat);
    if (inputMinutes === null)
        return options;
    const indicesToShow = new Set();
    const addWindow = (targetMins) => {
        let closestIdx = 0;
        let minDiff = Infinity;
        options.forEach((opt, idx) => {
            const diff = Math.abs(opt.minutes - targetMins);
            if (diff < minDiff) {
                minDiff = diff;
                closestIdx = idx;
            }
        });
        const start = Math.max(0, closestIdx - 2);
        const end = Math.min(options.length, closestIdx + 5);
        for (let i = start; i < end; i++)
            indicesToShow.add(i);
    };
    const hasPeriod = normalizedInput.includes('p') || normalizedInput.includes('a');
    if (hasPeriod || is24hFormat) {
        addWindow(inputMinutes);
    }
    else {
        addWindow(inputMinutes % HALF_DAY_MINUTES);
        addWindow((inputMinutes % HALF_DAY_MINUTES) + HALF_DAY_MINUTES);
    }
    return options.filter((_, idx) => indicesToShow.has(idx));
};
export const getHighlightedIndex = (inputValue, options, is24hFormat = false) => {
    if (!inputValue || options.length === 0)
        return 0;
    const normalizedInput = normalizeTime(inputValue);
    // 1. Exact match
    const exactIndex = options.findIndex((opt) => normalizeTime(opt.label) === normalizedInput);
    if (exactIndex !== -1)
        return exactIndex;
    // 2. Prefix match on normalized label (e.g., "1:00a" starts with "1")
    const prefixIndex = options.findIndex((opt) => normalizeTime(opt.label).startsWith(normalizedInput));
    if (prefixIndex !== -1)
        return prefixIndex;
    // 3. Prefix match without colons (e.g., "100a" starts with "1")
    const noColonIndex = options.findIndex((opt) => normalizeTime(opt.label).replace(/[:]/g, '').startsWith(normalizedInput));
    if (noColonIndex !== -1)
        return noColonIndex;
    // 4. Prefix match without colons and period (e.g., "100" starts with "1")
    const noPeriodIndex = options.findIndex((opt) => normalizeTime(opt.label).replace(/[ap:]/g, '').startsWith(normalizedInput));
    if (noPeriodIndex !== -1)
        return noPeriodIndex;
    // 5. Contains match
    const containsIndex = options.findIndex((opt) => normalizeTime(opt.label).includes(normalizedInput));
    if (containsIndex !== -1)
        return containsIndex;
    // 6. Try parsing as time and find closest
    const inputMinutes = parseInputToMinutes(inputValue, is24hFormat);
    if (inputMinutes !== null) {
        let closestIndex = 0;
        let minDiff = Infinity;
        options.forEach((opt, idx) => {
            const diff = Math.abs(opt.minutes - inputMinutes);
            if (diff < minDiff) {
                minDiff = diff;
                closestIndex = idx;
            }
        });
        return closestIndex;
    }
    // Default to first option
    return 0;
};
const parseTimeInput = (input) => {
    if (!input || input.trim() === '')
        return null;
    const normalized = normalizeTime(input);
    const timePart = normalized.replace(/[ap]/g, '');
    if (timePart.includes(':')) {
        const parts = timePart.split(':');
        if (parts.length !== 2 || !parts[1] || parts[1].length === 0)
            return null;
        const hour = parseInt(parts[0], 10);
        const minute = parseInt(parts[1], 10);
        if (isNaN(hour) || isNaN(minute))
            return null;
        if (minute < 0 || minute > 59)
            return null;
        if (hour < 0 || hour > 23)
            return null;
        // Minutes are complete if they have exactly 2 digits
        const hasCompleteMinutes = parts[1].length >= 2;
        return { hour, minute, hasExplicitMinutes: true, hasCompleteMinutes };
    }
    const numbers = timePart.replace(/[^0-9]/g, '');
    if (!numbers)
        return null;
    let hour;
    let minute;
    let hasExplicitMinutes;
    if (numbers.length <= 2) {
        hour = parseInt(numbers, 10);
        minute = 0;
        hasExplicitMinutes = false;
    }
    else {
        const hourPart = numbers.slice(0, numbers.length - 2);
        const minPart = numbers.slice(numbers.length - 2);
        hour = parseInt(hourPart, 10);
        minute = parseInt(minPart, 10);
        hasExplicitMinutes = true;
    }
    if (isNaN(hour) || isNaN(minute))
        return null;
    if (minute < 0 || minute > 59)
        return null;
    if (hour < 0 || hour > 23)
        return null;
    return { hour, minute, hasExplicitMinutes, hasCompleteMinutes: hasExplicitMinutes };
};
export const isValidTimeFormat = (input) => {
    return parseTimeInput(input) !== null;
};
export const isFullyQualifiedTime = (input) => {
    const parsed = parseTimeInput(input);
    return parsed !== null && parsed.hasExplicitMinutes && parsed.hasCompleteMinutes;
};
export const parseInitialValue = (value, is24hFormat = false) => {
    if (!value || value.length === 0 || !value[0])
        return '';
    const singleValue = value[0];
    const normalized = normalizeTime(singleValue);
    // 12h format
    if (!is24hFormat && (normalized.includes('a') || normalized.includes('p'))) {
        const mins = parseInputToMinutes(singleValue, false);
        if (mins !== null) {
            return formatMinutesToTime(mins, false);
        }
        return singleValue;
    }
    // 24h format
    const timeParts = singleValue.split(':');
    if (timeParts.length >= 2) {
        const hour = parseInt(timeParts[0], 10);
        const minute = parseInt(timeParts[1], 10);
        if (!isNaN(hour) && !isNaN(minute) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
            const totalMinutes = hour * MINUTES_PER_HOUR + minute;
            return formatMinutesToTime(totalMinutes, is24hFormat);
        }
    }
    // fallback
    const mins = parseInputToMinutes(singleValue, true);
    if (mins !== null) {
        return formatMinutesToTime(mins, is24hFormat);
    }
    return singleValue;
};
//# sourceMappingURL=BKJTimeInput.utils.js.map